import { SmartCreationReviewError } from "@adpilot/shared";
import { HTTPError, TimeoutError } from "ky";
import { i18n } from "@/i18n";

/**
 * 后端错误契约：稳定错误码 + 英文 message。
 * UI 文案一律由前端按 error.code 从 errors namespace 本地化，
 * 后端 message 只作为码表未覆盖时的兜底（ADP-74）。
 */
interface ApiErrorBody {
  success: false;
  error: {
    code: string;
    message: string;
    details?: unknown;
    request_id?: string;
  };
}

export async function getApiErrorCode(error: unknown): Promise<string | null> {
  if (error instanceof SmartCreationReviewError) return error.code;
  if (!(error instanceof HTTPError)) return null;
  const body = await error.response
    .clone()
    .json()
    .catch(() => null);
  return isApiErrorBody(body) ? body.error.code : null;
}

export async function getApiErrorDetails(error: unknown): Promise<unknown> {
  if (error instanceof SmartCreationReviewError) return error.details;
  if (!(error instanceof HTTPError)) return undefined;
  const body = await error.response
    .clone()
    .json()
    .catch(() => null);
  return isApiErrorBody(body) ? body.error.details : undefined;
}

function isApiErrorBody(body: unknown): body is ApiErrorBody {
  if (typeof body !== "object" || body === null) return false;
  const error = (body as { error?: unknown }).error;
  return (
    typeof error === "object" &&
    error !== null &&
    typeof (error as { code?: unknown }).code === "string"
  );
}

/**
 * 把任意异常（ky HTTPError / TimeoutError / 其他）解析为当前语言的用户可读文案。
 * 用法：toast.error(await getApiErrorMessage(err))
 */
export async function getApiErrorMessage(error: unknown): Promise<string> {
  if (error instanceof SmartCreationReviewError) {
    const key = `errors:${error.code}`;
    return i18n.exists(key) ? i18n.t(key) : error.message;
  }
  if (error instanceof HTTPError) {
    const body = await error.response
      .clone()
      .json()
      .catch(() => null);
    if (isApiErrorBody(body)) {
      const key = `errors:${body.error.code}`;
      if (i18n.exists(key)) return i18n.t(key);
      // 码表未覆盖：优先后端 message，避免展示空白
      if (body.error.message) return body.error.message;
    }
    return i18n.t("errors:network.generic");
  }
  if (error instanceof TimeoutError) {
    return i18n.t("errors:network.timeout");
  }
  if (error instanceof TypeError) {
    // fetch 网络层失败（断网、DNS 等）表现为 TypeError
    return i18n.t("errors:network.generic");
  }
  return i18n.t("errors:unknown");
}
